import { z } from 'zod';

import { SharedLib } from '@shared';

import { ACT_STATUSES, isActStatus } from '@units/act/model/enums/act-status.enums.js';
import { type ActStatus } from '@units/act/types/act.types.js';

/**
 * Состояние реестра актов — так, как его несёт адресная строка (`rules/lists-and-filters.mdc`).
 *
 * Весь отбор живёт здесь, а не в состоянии компонента: «все инструкции, отменённые к марту» — это
 * ссылка, которую юрист пересылает коллеге, а не последовательность нажатий, которую он диктует.
 *
 * **Каждое поле падает в умолчание через `.catch`, а не бросает.** Проваленный `validateSearch`
 * заменяет экран границей ошибки, и со `replace: true` битый адрес остаётся в строке — перезагрузка
 * не помогает. Сервер поступает наоборот и отвечает 422: там неизвестное значение прислал клиент, и
 * правдоподобная подстановка спрятала бы дефект.
 */

/** Столько же, сколько принимает `q` у `GET /acts`: искать длиннее нечего. */
const MAX_QUERY = 300;

/** Двадцать пять строк — тот же размер, что в реестре документов: экраны читаются вместе. */
export const ACTS_PER_PAGE = 25;

/** Потолок совпадает с серверным `perPage: maximum 100`: просить больше значит получить урезанное. */
export const ACT_PER_PAGE_OPTIONS = [25, 50, 100] as const;

/**
 * Статусы — поэлементным whitelist'ом, а не «весь список или ничего».
 *
 * `.catch([])` на массиве целиком отбросил бы **весь** отбор из-за одного незнакомого слова: ссылка,
 * пережившая переименование статуса на сервере, показала бы не суженный перечень, а полный — то есть
 * тихо ответила бы на другой вопрос. Здесь незнакомое выпадает поштучно.
 *
 * Молчание — «без отбора», вместе с проектами и отменёнными. Это противоположно `circulation` у
 * документов, где молчание значит «в обращении», и разница намеренная: отменённый акт остаётся
 * частью ответа на вопрос «что действовало», и спрятать его умолчанием значило бы стереть историю
 * способом отображения.
 */
const statusFilter = z
  /*
   * `z.preprocess`, а не `z.unknown().transform(...)`: внутри `z.object` схема, чей вход
   * необязателен, отвечает «expected nonoptional» на отсутствующий параметр — то есть роняет экран
   * ровно на пустом адресе.
   */
  .preprocess(
    (value): unknown[] =>
      value === undefined ? [] : Array.isArray(value) ? (value as unknown[]) : [value],
    z.array(z.unknown()),
  )
  .transform((values): readonly ActStatus[] => {
    const chosen = values.filter(isActStatus);

    // Канонический порядок и тем самым отсутствие повторов: отбор входит в ключ кеша, и `A,B`
    // против `B,A` завели бы две записи под один ответ.
    return ACT_STATUSES.filter((status) => chosen.includes(status));
  });

export const actRegistrySearchSchema = z.object({
  q: z.string().trim().max(MAX_QUERY).catch('').default(''),
  status: statusFilter,
  page: z.coerce
    .number()
    .int()
    .min(1)
    .catch(SharedLib.DEFAULT_PAGE)
    .default(SharedLib.DEFAULT_PAGE),
  perPage: SharedLib.perPageWhitelistSchema(ACT_PER_PAGE_OPTIONS, ACTS_PER_PAGE),
});

export type ActRegistrySearch = z.infer<typeof actRegistrySearchSchema>;

/**
 * Что считается **фильтром** — то, смена чего возвращает читателя на первую страницу.
 *
 * Порядка сортировки здесь нет, потому что его нет и у операции: реестр упорядочен по наименованию,
 * и это решение сервера (`GET /acts`), а не выбор экрана.
 */
export const ACT_FILTER_FIELDS = ['q', 'status'] as const;
